import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Card,
  Col,
  Empty,
  Flex,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CompassOutlined,
  KeyOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import PropTypes from 'prop-types';
import {
  firListenSiteDoorEvents,
  firListenDoorHeartbeats,
  firGetDoorChannels,
} from '../firebase';
import notValid from '../common/notValid';
import { scrollHeight } from '../common/constants';

dayjs.extend(relativeTime);

const { Text } = Typography;

const STATUS_META = {
  requested: { color: 'gold',   icon: <ClockCircleOutlined />, label: 'Sent' },
  delivered: { color: 'blue',   icon: <ClockCircleOutlined />, label: 'Door received' },
  opened:    { color: 'green',  icon: <CheckCircleOutlined />, label: 'Opened' },
  failed:    { color: 'red',    icon: <CloseCircleOutlined />, label: 'Failed' },
};

/**
 * Detect whether an event was initiated remotely (mobile / web app) or
 * locally at the door (button / keypad / RFID reader).
 *
 * Heuristic: mobile-initiated events have a `requested` entry with
 * source="mobile" in events[]; local-initiated events (Pi-generated
 * requestIds for keypad/reader/button opens) only ever contain
 * "opened" with source="pi". The Pi doesn't currently report WHICH
 * local source (keypad vs reader vs button) — that's a future schema
 * extension (FIRESTORE_LISTENER_MIGRATION.md §11).
 */
/**
 * Render a string with embedded http(s) URLs as clickable anchors.
 * antd Alert.description renders text as-is; Firestore error messages
 * include very long composite-index creation URLs that we want users
 * to be able to click in one go.
 */
function linkifyText(text) {
  if (!text) return null;
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
          {part}
        </a>
      );
    }
    return part;
  });
}

function detectOrigin(event) {
  const evs = event.events || [];
  if (evs.some((e) => e.type === 'requested' && e.source === 'mobile')) return 'mobile';
  return 'local';
}

/** Time from `requested` to `opened` in ms, or null if not measurable. */
function computeLatency(event) {
  const evs = event.events || [];
  const requested = evs.find((e) => e.type === 'requested');
  const opened = evs.find((e) => e.type === 'opened');
  if (!requested || !opened) return null;
  const reqMs = Date.parse(requested.at);
  const opMs = Date.parse(opened.at);
  if (!Number.isFinite(reqMs) || !Number.isFinite(opMs)) return null;
  return opMs - reqMs;
}

function formatLatency(ms) {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Online / offline / never-seen status for a door, computed from its
 * heartbeat doc's lastSeenAt field. Threshold: 3 min = 3× the default
 * 60s Pi heartbeat interval, so we tolerate 2 missed beats before
 * flipping to offline.
 */
const ONLINE_FRESHNESS_MS = 3 * 60 * 1000;

function statusFromHeartbeat(heartbeat) {
  if (!heartbeat || !heartbeat.lastSeenAt?.toMillis) {
    return { code: 'never', badgeStatus: 'default', text: 'Never connected' };
  }
  const lastSeenMs = heartbeat.lastSeenAt.toMillis();
  const ageMs = Date.now() - lastSeenMs;
  if (ageMs < ONLINE_FRESHNESS_MS) {
    return { code: 'online', badgeStatus: 'success', text: 'Online' };
  }
  const ago = dayjs(lastSeenMs).fromNow();
  return { code: 'offline', badgeStatus: 'error', text: `Offline (last seen ${ago})` };
}

/**
 * Per-door summary card: door label, real Online/Offline indicator
 * (from heartbeat doc, rev 121.1), last-open recency + source, today's
 * open count.
 */
function DoorSummaryCard({ doorId, doorLabel, eventsForDoor, heartbeat }) {
  const lastOpened = eventsForDoor.find((e) => e.status === 'opened');
  const todayCount = useMemo(() => {
    const startOfToday = dayjs().startOf('day').valueOf();
    return eventsForDoor.filter((e) => {
      if (e.status !== 'opened') return false;
      const ts = e.createdAt?.toMillis ? e.createdAt.toMillis() : 0;
      return ts >= startOfToday;
    }).length;
  }, [eventsForDoor]);

  const lastOpenedAt = lastOpened?.createdAt?.toDate ? lastOpened.createdAt.toDate() : null;
  const lastAgo = lastOpenedAt ? dayjs(lastOpenedAt).fromNow() : 'never';
  const lastOrigin = lastOpened ? detectOrigin(lastOpened) : null;

  const status = statusFromHeartbeat(heartbeat);
  const lastSeenAbs = heartbeat?.lastSeenAt?.toDate
    ? dayjs(heartbeat.lastSeenAt.toDate()).format('YYYY-MM-DD HH:mm:ss')
    : null;

  return (
    <Card size="small" style={{ height: '100%' }}>
      <Flex justify="space-between" align="start" wrap="wrap">
        <div>
          <Text strong style={{ fontSize: 16 }}>Door {doorId}</Text>
          {doorLabel ? <Text type="secondary" style={{ marginLeft: 8 }}>{doorLabel}</Text> : null}
        </div>
        {lastSeenAbs ? (
          <Tooltip title={`Last heartbeat: ${lastSeenAbs}`}>
            <Badge status={status.badgeStatus} text={status.text} />
          </Tooltip>
        ) : (
          <Badge status={status.badgeStatus} text={status.text} />
        )}
      </Flex>
      <Row gutter={16} style={{ marginTop: 12 }}>
        <Col span={12}>
          <Statistic
            title="Last open"
            value={lastAgo}
            valueStyle={{ fontSize: 14 }}
          />
          {lastOrigin ? (
            <Tag
              icon={lastOrigin === 'mobile' ? <MobileOutlined /> : <KeyOutlined />}
              color={lastOrigin === 'mobile' ? 'blue' : 'purple'}
              style={{ marginTop: 4 }}
            >
              {lastOrigin === 'mobile' ? 'Mobile / web' : 'Local (keypad/reader/button)'}
            </Tag>
          ) : null}
        </Col>
        <Col span={12}>
          <Statistic title="Opens today" value={todayCount} />
        </Col>
      </Row>
    </Card>
  );
}

DoorSummaryCard.propTypes = {
  doorId: PropTypes.string.isRequired,
  doorLabel: PropTypes.string,
  eventsForDoor: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    doorId: PropTypes.string,
  })).isRequired,
  heartbeat: PropTypes.shape({
    id: PropTypes.string,
    deviceId: PropTypes.string,
    doorId: PropTypes.string,
    lastSeenAt: PropTypes.shape({
      toMillis: PropTypes.func,
      toDate: PropTypes.func,
    }),
  }),
};

DoorSummaryCard.defaultProps = {
  doorLabel: '',
  heartbeat: null,
};

/** Render the row of per-door cards, the "no doors yet" empty state,
 * or the loading skeleton. Lifted out as a helper to avoid nested
 * ternaries in the JSX. */
function renderDoorCards({ doorsLoading, doorIdList, doors, eventsByDoor, heartbeatsByDoor }) {
  if (doorsLoading) {
    return (<Col span={24}><Card loading /></Col>);
  }
  if (doorIdList.length === 0) {
    return (
      <Col span={24}>
        <Card>
          <Empty
            image={<CompassOutlined style={{ fontSize: 48, color: '#bbb' }} />}
            description="No doors configured for this site yet (configure under Door Channels)."
          />
        </Card>
      </Col>
    );
  }
  return doorIdList.map((d) => (
    <Col key={d} xs={24} sm={12} md={8} lg={6}>
      <DoorSummaryCard
        doorId={d}
        doorLabel={doors[d]?.label}
        eventsForDoor={eventsByDoor[d] || []}
        heartbeat={heartbeatsByDoor[d] || null}
      />
    </Col>
  ));
}

/**
 * Door Activity tab — live feed of doorEvents for this site, with
 * per-door summary cards on top and a sortable/filterable event
 * table below. Real-time via Firestore onSnapshot.
 */
export default function SiteConfigDoorStatus({ siteId }) {
  const [events, setEvents] = useState([]);
  const [heartbeats, setHeartbeats] = useState([]);
  const [doors, setDoors] = useState({});  // channel code -> { isVisitorEntry, label }
  const [doorsLoading, setDoorsLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [doorsError, setDoorsError] = useState(null);
  const [eventsError, setEventsError] = useState(null);
  const [filterDoor, setFilterDoor] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterOrigin, setFilterOrigin] = useState(null);
  // Tick every 30s so "Offline (last seen 5 min ago)" relative-time
  // text stays accurate without waiting for the next heartbeat.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (notValid(siteId)) {
      // No siteId yet (parent still resolving the user's assigned
      // site). Don't spin forever — show empty state instead.
      setDoorsLoading(false);
      setEventsLoading(false);
      return undefined;
    }

    // One-shot fetch of door labels for the per-door cards + filter
    // dropdown. Refetching on every tab open is overkill — door
    // channels rarely change.
    setDoorsLoading(true);
    setDoorsError(null);
    firGetDoorChannels(siteId)
      .then((d) => setDoors(d || {}))
      .catch((err) => {
        console.error('-- fetch doors failed:', err);
        setDoorsError(err);
      })
      .finally(() => setDoorsLoading(false));

    // Live event stream. Crucial: the onError path MUST flip
    // eventsLoading off — otherwise a missing Firestore composite
    // index (`siteId ASC, createdAt DESC` on doorEvents) makes the
    // listener fail silently and the table spinner spins forever.
    // The error itself usually contains a console-clickable URL to
    // create the index — surface it via setEventsError so the admin
    // sees it without opening DevTools.
    setEventsLoading(true);
    setEventsError(null);
    const unsub = firListenSiteDoorEvents(siteId, (evs) => {
      setEvents(evs);
      setEventsLoading(false);
    }, {
      onError: (err) => {
        console.error('-- doorEvents listener error:', err);
        setEventsError(err);
        setEventsLoading(false);
      },
    });

    // Heartbeat stream — separate listener, separate cleanup. No
    // composite index needed (single where filter only).
    const unsubHb = firListenDoorHeartbeats(siteId, setHeartbeats, {
      onError: (err) => {
        console.error('-- doorHeartbeats listener error:', err);
        // Non-fatal: cards just fall back to "Never connected" badge.
      },
    });

    return () => {
      if (typeof unsub === 'function') unsub();
      if (typeof unsubHb === 'function') unsubHb();
    };
  }, [siteId]);

  // Index heartbeats by doorId for fast lookup. If multiple Pis ever
  // map to the same doorId (shouldn't happen but defensive), keep the
  // freshest.
  const heartbeatsByDoor = useMemo(() => {
    const map = {};
    heartbeats.forEach((hb) => {
      if (!hb.doorId) return;
      const existing = map[hb.doorId];
      const newer = !existing
        || (hb.lastSeenAt?.toMillis?.() || 0) > (existing.lastSeenAt?.toMillis?.() || 0);
      if (newer) map[hb.doorId] = hb;
    });
    return map;
  }, [heartbeats]);

  // Group events by doorId for per-door cards
  const eventsByDoor = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const d = e.doorId || '(unknown)';
      if (!map[d]) map[d] = [];
      map[d].push(e);
    });
    return map;
  }, [events]);

  // List of door IDs to render cards for: union of currently-known
  // doors (from sites/{siteId}.information.doorChannel) and any
  // doorId seen in events. The latter catches doors that fired
  // events but were renamed / deleted in the door channel config.
  const doorIdList = useMemo(() => {
    const set = new Set(Object.keys(doors));
    events.forEach((e) => { if (e.doorId) set.add(e.doorId); });
    return Array.from(set).sort();
  }, [doors, events]);

  // Filter dropdown options
  const doorOptions = useMemo(() => [
    { value: null, label: 'All doors' },
    ...doorIdList.map((d) => ({
      value: d,
      label: `Door ${d}${doors[d]?.label ? ` — ${doors[d].label}` : ''}`,
    })),
  ], [doorIdList, doors]);

  const filteredEvents = useMemo(() => events.filter((e) => {
    if (filterDoor && e.doorId !== filterDoor) return false;
    if (filterStatus && e.status !== filterStatus) return false;
    if (filterOrigin && detectOrigin(e) !== filterOrigin) return false;
    return true;
  }), [events, filterDoor, filterStatus, filterOrigin]);

  const columns = [
    {
      title: 'When',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v) => {
        if (!v?.toDate) return '—';
        const d = dayjs(v.toDate());
        return (
          <Tooltip title={d.format('YYYY-MM-DD HH:mm:ss')}>
            <Text>{d.fromNow()}</Text>
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Door',
      dataIndex: 'doorId',
      key: 'doorId',
      width: 150,
      render: (v) => {
        const label = doors[v]?.label;
        return (
          <Space>
            <code>{v || '?'}</code>
            {label ? <Text type="secondary">{label}</Text> : null}
          </Space>
        );
      },
    },
    {
      title: 'Trigger',
      key: 'origin',
      width: 220,
      render: (_, record) => {
        const origin = detectOrigin(record);
        return origin === 'mobile' ? (
          <Tag icon={<MobileOutlined />} color="blue">Mobile / web</Tag>
        ) : (
          <Tag icon={<KeyOutlined />} color="purple">Local door input</Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (v) => {
        const meta = STATUS_META[v] || { color: 'default', label: v };
        return <Tag color={meta.color} icon={meta.icon}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (v) => v ? <Text type="secondary">{v}</Text> : '—',
    },
    {
      title: 'Latency',
      key: 'latency',
      width: 100,
      render: (_, record) => {
        const ms = computeLatency(record);
        return formatLatency(ms);
      },
    },
    {
      title: 'requestId',
      dataIndex: 'requestId',
      key: 'requestId',
      width: 110,
      render: (v) => v ? (
        <Tooltip title={v}>
          <code style={{ fontSize: 11 }}>{v.slice(0, 8)}…</code>
        </Tooltip>
      ) : '—',
    },
  ];

  return (
    <>
      {eventsError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Door events listener failed to start."
          description={(
            <>
              <div>
                <code>{eventsError.code || eventsError.name || 'error'}</code>:{' '}
                {linkifyText(eventsError.message || String(eventsError))}
              </div>
              <div style={{ marginTop: 8 }}>
                Most common cause: the Firestore composite index for
                {' '}<code>doorEvents (siteId ASC, createdAt DESC)</code> hasn&apos;t been
                deployed. Click the console URL above to create it one-off, or
                run
                {' '}<code>firebase deploy --only firestore:indexes</code>
                {' '}from the management web project root.
              </div>
            </>
          )}
        />
      ) : null}
      {doorsError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Door labels failed to load."
          description={(
            <div>
              <code>{doorsError.code || doorsError.name || 'error'}</code>:{' '}
              {linkifyText(doorsError.message || String(doorsError))}
            </div>
          )}
        />
      ) : null}
      {!eventsError && !doorsError ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Live view — ${events.length} recent event${events.length === 1 ? '' : 's'} loaded. New opens appear automatically.`}
          description="Each door open — whether from the app, a card swipe, the keypad, or the door's unlock button — gets a row here within ~1 second. Each door's controller also pings the cloud every minute; if more than ~3 minutes pass without a ping the card flips to Offline."
        />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {renderDoorCards({ doorsLoading, doorIdList, doors, eventsByDoor, heartbeatsByDoor })}
      </Row>

      <Flex gap={12} wrap="wrap" style={{ marginBottom: 12 }}>
        <Select
          value={filterDoor}
          onChange={setFilterDoor}
          options={doorOptions}
          style={{ minWidth: 240 }}
          placeholder="Filter by door"
        />
        <Select
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: null, label: 'All statuses' },
            { value: 'requested', label: 'Sent' },
            { value: 'delivered', label: 'Pi received' },
            { value: 'opened',    label: 'Opened' },
            { value: 'failed',    label: 'Failed' },
          ]}
          style={{ minWidth: 180 }}
          placeholder="Filter by status"
        />
        <Select
          value={filterOrigin}
          onChange={setFilterOrigin}
          options={[
            { value: null, label: 'All triggers' },
            { value: 'mobile', label: 'Mobile / web' },
            { value: 'local',  label: 'Local door input' },
          ]}
          style={{ minWidth: 180 }}
          placeholder="Filter by trigger"
        />
        <Text type="secondary" style={{ alignSelf: 'center' }}>
          {filteredEvents.length} of {events.length} recent events shown.
        </Text>
      </Flex>

      <Table
        columns={columns}
        dataSource={filteredEvents}
        rowKey="id"
        loading={eventsLoading}
        pagination={{ pageSize: 25 }}
        scroll={{ y: scrollHeight }}
        size="small"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={(
                <>
                  <div>No door open attempts yet.</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Trigger an open from mobile/web or use a keypad/RFID/button at the door — it should appear here within ~1 second.
                  </Text>
                </>
              )}
            />
          ),
        }}
      />
    </>
  );
}

SiteConfigDoorStatus.propTypes = {
  siteId: PropTypes.string.isRequired,
};
